import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";
import styles from "./ReviewPhotoGallery.scss";
import getWindow from "../../utils/window";
import BrandedSpinner from "../spinner/branded/BrandedSpinner";
import useTrapFocus from "../../hooks/useTrapFocus";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import ImageGallery from "../imageGallery/ImageGallery";
import {
  ImageGalleryProvider,
  useImageGalleryContext,
} from "../imageGallery/ImageGalleryContext";
import { mapPhotoGalleryProps } from "./mapPhotoGalleryProps";
import { ReviewPhotoGalleryComponentProps } from "../../types/components/reviewPhotoGallery";
import { MapImageArgs } from "../../types/components/imageGallery";

function ReviewPhotoGalleryContent({
  togglePhotoGallery,
  gallery,
  screenHoverable,
}: ReviewPhotoGalleryComponentProps) {
  const { goToNext, goToPrevious } = useImageGalleryContext();
  const [overlayId] = useState(`overlay${Math.floor(Math.random() * 10000)}`);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [galleryWidth, setGalleryWidth] = useState(0);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string, params?: Record<string, unknown>) => string;
  };

  const handleGalleryKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" || event.keyCode === 27) {
      event.preventDefault();
      event.stopPropagation();
      togglePhotoGallery(false);
      return;
    }
    if (
      !["ArrowLeft", "ArrowRight"].includes(event.key) ||
      event.defaultPrevented ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      gallery.reviewPhotoList.length < 2 ||
      (event.target instanceof Element &&
        event.target.closest(
          'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
        ))
    ) {
      return;
    }

    event.preventDefault();
    if (event.key === "ArrowRight") goToNext();
    else goToPrevious();
  };

  useLayoutEffect(() => {
    if (!gallery.reviewPhotoList.length) return;

    const galleryElement = backButtonRef.current?.parentElement;
    if (!galleryElement) return;

    const modalBackdrop =
      galleryElement.parentElement?.closest(
        '[role="dialog"]',
      )?.previousElementSibling;
    const document = galleryElement.ownerDocument;
    const focusOnClose =
      gallery.targetElement?.closest("button") ??
      gallery.targetElement ??
      document.activeElement;
    const hiddenElements: {
      element: Element;
      inert: string | null;
      ariaHidden: string | null;
    }[] = [];

    backButtonRef.current?.focus({ preventScroll: true });

    let branch: HTMLElement | null = galleryElement;
    while (branch?.parentElement && branch !== document.body) {
      for (const element of Array.from(branch.parentElement.children)) {
        if (element === branch || element === modalBackdrop) continue;

        hiddenElements.push({
          element,
          inert: element.getAttribute("inert"),
          ariaHidden: element.getAttribute("aria-hidden"),
        });
        element.setAttribute("inert", "");
        element.setAttribute("aria-hidden", "true");
      }
      branch = branch.parentElement;
    }

    return () => {
      for (const { element, inert, ariaHidden } of hiddenElements) {
        if (inert === null) element.removeAttribute("inert");
        else element.setAttribute("inert", inert);
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      }
      if (focusOnClose instanceof HTMLElement && focusOnClose.isConnected) {
        focusOnClose.focus({ preventScroll: true });
      }
    };
  }, [gallery.reviewPhotoList.length, gallery.targetElement]);

  useEffect(() => {
    const sideModal = getWindow().document.querySelector(
      "[data-testid='sideModal']",
    );
    const sideModalWidth = sideModal!.getBoundingClientRect().width;

    setGalleryWidth(sideModalWidth);
  }, []);

  const setModalRef = useTrapFocus();

  useEffect(() => {
    setModalRef(backButtonRef.current?.parentElement);
  }, [setModalRef]);

  const mapImage = ({
    image,
    index,
    displayedImageIndex,
    ariaHiddenOverride,
  }: MapImageArgs) => {
    return (
      <img
        style={{ width: galleryWidth }}
        key={image.url}
        className={styles.image}
        data-testid="reviewsPhotoGalleryImage"
        alt={`${getTranslation(
          "rdp_ratings_reviews_accessibility_image_description",
        )}. ${getTranslation("pdp_ratings_review_gallery_image_count", {
          index: index + 1,
          total: gallery.reviewPhotoList.length,
        })}`}
        src={image.url}
        onLoad={() => setImageLoaded(true)}
        aria-hidden={ariaHiddenOverride || displayedImageIndex !== index}
      />
    );
  };

  return gallery?.reviewPhotoList.length ? (
    <div
      className={styles.container}
      id={overlayId}
      role="dialog"
      aria-modal="true"
      aria-label={getTranslation("pdp_ratings_reviews_reviews_title")}
      onKeyDown={handleGalleryKeyDown}
    >
      <button
        className={styles.backBtn}
        data-testid="reviewsPhotoGalleryBackButton"
        aria-label={getTranslation("pdp_misc_back")}
        onClick={() => togglePhotoGallery(false)}
        ref={backButtonRef}
      >
        <div className={"product-left-arrow-outlined"} />
      </button>
      {!imageLoaded && <BrandedSpinner colour={"white"} />}
      <ImageGallery
        screenHoverable={screenHoverable}
        mapImage={mapImage}
        ariaLive="polite"
      />
    </div>
  ) : null;
}

export default function ReviewPhotoGallery({
  gallery,
  initialIndex = 0,
  ...props
}: ReviewPhotoGalleryComponentProps) {
  return (
    <ImageGalleryProvider
      images={mapPhotoGalleryProps(gallery)}
      initialIndex={initialIndex}
    >
      <ReviewPhotoGalleryContent gallery={gallery} {...props} />
    </ImageGalleryProvider>
  );
}
